import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Bar } from 'react-chartjs-2';
import { AppBar, Grid, Typography } from '@material-ui/core';
import InputLabel from '@material-ui/core/InputLabel';
import MUIFormControl from '@material-ui/core/FormControl';
import MUISelect from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getMarketName } from 'features/app/selectors';
import { fetchCoWorkerEngagementReport } from 'features/PpsiContainer/actions';
import {
  getIsFetchingInteractionReport,
  getTotalInteractionReport
} from 'features/PpsiContainer/selectors';
import ProgressFullscreen from 'components/common/ProgressFullscreen';
import MUIToolbar from '@material-ui/core/Toolbar';
import GoBackButton from 'components/Form/Toolbar/GoBackButton';
import { lighten } from '@material-ui/core/styles/colorManipulator';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const useStyles = makeStyles(theme => ({
  appBar: {
    position: 'relative', // To make z-index work
    zIndex: theme.zIndex.drawer + 1,
    color: theme.palette.text.primary,
    background: lighten(theme.palette.primary.light, 0.97)
  },
  container: {
    padding: theme.spacing(4),
    height: 'calc(100% - 64px)'
  },
  header: {
    backgroundColor: '#fafafa',
    height: 64,
    paddingTop: 16,
    borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
  },
  headerText: {
    paddingLeft: 16,
    fontSize: '1.25rem',
    fontFamily: 'Roboto',
    fontWeight: 500,
    lineHeight: '1.6'
  },
  paper: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 5,
    backgroundColor: '#fff',
    paddingBottom: 15,
    borderRadius: 4,
    paddingLeft: 30,
    boxShadow:
      '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)'
  },
  noDataText: {
    padding: theme.spacing(4),
    textAlign: 'center'
  },
  chartWrapper: {
    width: '100%',
    overflowX: 'auto'
  },
  chartContainer: {
    height: '300px',
    minWidth: '100%'
  },
  extendedChart: {
    width: '3000px'
  }
}));

const CoWorkerEngagementReportView = () => {
  const classes = useStyles();
  const { id } = useParams();
  const dispatch = useDispatch();
  const currOrganization = useSelector(getMarketName);
  const totalEngagementReport = useSelector(getTotalInteractionReport);
  const isFetchingEngagementReport = useSelector(
    getIsFetchingInteractionReport
  );

  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [timeFrame, setTimeFrame] = useState('monthly');
  const [chartData, setChartData] = useState({ labels: [], datasets: [] });
  const [totalCount, setTotalCount] = useState(0);
  const [hasData, setHasData] = useState(false);
  const [weekRanges, setWeekRanges] = useState([]);
  const engagementReport = totalEngagementReport.toJS();
  const marketCode = id;

  const currentYear = new Date().getFullYear();
  const years = [];
  for (let y = 2020; y <= currentYear; y++) {
    years.push(y.toString());
  }

  useEffect(() => {
    if (currOrganization) {
      const data = {
        market: currOrganization === 'IFS' ? marketCode : currOrganization,
        year
      };
      dispatch(fetchCoWorkerEngagementReport(data));
    }
  }, [dispatch, year, currOrganization, marketCode]);

  useEffect(() => {
    if (totalEngagementReport) {
      if (engagementReport.length > 0) {
        setHasData(true);
        const { labels, weekRanges } = generateLabels(year, timeFrame);
        setWeekRanges(weekRanges);
        const data = aggregateData(engagementReport, timeFrame, labels);
        const values = labels.map(label => data[label] || 0);

        setChartData({
          labels,
          datasets: [
            {
              label: 'Interactions',
              data: values,
              backgroundColor: 'rgba(54, 162, 235, 0.2)',
              borderColor: 'rgba(54, 162, 235, 1)',
              borderWidth: 1,
              fill: true
            }
          ]
        });
      } else {
        setHasData(false);
      }
    }
  }, [totalEngagementReport, timeFrame, year]);
  useEffect(() => {
    if (engagementReport.length) {
      let count = engagementReport.reduce(
        (acc, val) => acc + Number(val.interactioncount),
        0
      );
      setTotalCount(count);
    } else {
      setTotalCount(0);
    }
  }, [engagementReport, year]);

  const generateLabels = (year, period) => {
  const labels = [];
  const weekRanges = [];
  const now = new Date();
  const isCurrentYear = parseInt(year, 10) === now.getFullYear();

  switch (period) {
    case 'daily':
      const start = new Date(Date.UTC(year, 0, 1));
      const end = isCurrentYear
        ? new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()))
        : new Date(Date.UTC(year, 11, 31));

      let current = new Date(start);

      while (current <= end) {
        const yyyy = current.getUTCFullYear();
        const mm = String(current.getUTCMonth() + 1).padStart(2, '0');
        const dd = String(current.getUTCDate()).padStart(2, '0');

        labels.push(`${yyyy}-${mm}-${dd}`);
        current.setUTCDate(current.getUTCDate() + 1);
      }
      break;

    case 'weekly':
      const currentWeekStart = getStartOfFirstWeek(year);
      const totalWeeks = isCurrentYear
        ? getWeekNumber(now)
        : getWeekNumber(new Date(year, 11, 31));

      for (let i = 1; i <= totalWeeks; i++) {
        const { start, end } = getWeekRange(currentWeekStart);
        labels.push(`Week ${i}`);
        weekRanges.push(
          `Week ${i}: ${start.toLocaleDateString()} - ${end.toLocaleDateString()}`
        );
        currentWeekStart.setUTCDate(currentWeekStart.getUTCDate() + 7);
      }
      break;

    case 'monthly':
    default:
      labels.push('Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec');
      if (isCurrentYear) {
        labels.splice(now.getMonth() + 1);
      }
      break;
  }

  return { labels, weekRanges };
};


const aggregateData = (data, period, labels) => {
  const result = {};

  data.forEach(item => {
    // Extract YYYY-MM-DD
    const [year, month, day] = item.interactiondate.split('-').map(Number);

    // Create UTC date (NO timezone shift)
    const nDate = new Date(Date.UTC(year, month - 1, day));

    let key;

    switch (period) {
      case 'daily': {
        const yyyy = nDate.getUTCFullYear();
        const mm = String(nDate.getUTCMonth() + 1).padStart(2, '0');
        const dd = String(nDate.getUTCDate()).padStart(2, '0');
        key = `${yyyy}-${mm}-${dd}`;
        break;
      }

      case 'weekly':
        key = `Week ${getWeekNumber(nDate)}`;
        break;

      case 'monthly':
      default:
        key = labels[nDate.getUTCMonth()];
        break;
    }

    if (!result[key]) {
      result[key] = 0;
    }
    result[key] += parseInt(item.interactioncount, 10);
  });

  return result;
};


  const getStartOfWeek = date => {
    const d = new Date(date);
    const day = d.getDay(); // Sunday = 0
    const start = new Date(d);
    start.setDate(d.getDate() - day); // go back to Sunday
    start.setHours(0, 0, 0, 0);
    return start;
  };

  const getStartOfFirstWeek = year => {
    const jan1 = new Date(year, 0, 0); // Jan 1st
    return getStartOfWeek(jan1); // first Sunday on or before Jan 1st
  };

  const getWeekNumber = dateInput => {
    const date = new Date(dateInput);
    if (isNaN(date)) return NaN;

    const year = date.getUTCFullYear();
    const jan1 = new Date(Date.UTC(year, 0, 1));
    const jan1Day = jan1.getUTCDay(); // 0 = Sunday

    // First Sunday after Jan 1
    const firstSunday = new Date(jan1);
    if (jan1Day === 0) {
      firstSunday.setUTCDate(jan1.getUTCDate() + 1);
    } else {
      firstSunday.setUTCDate(jan1.getUTCDate() + (7 - jan1Day));
    }

    if (date < firstSunday) {
      return 1;
    }

    const diffTime = date.getTime() - firstSunday.getTime();
    const daysDiff = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    return Math.floor(daysDiff / 7) + 2;
  };

  const getWeekRange = date => {
    const start = new Date(date);
    const end = new Date(date);
    end.setDate(start.getDate() + 6);

    return { start, end };
  };

  Date.prototype.getDayOfYear = function() {
    const start = new Date(this.getFullYear(), 0, 0);
    const diff = this - start;
    const oneDay = 1000 * 60 * 60 * 24;
    return Math.floor(diff / oneDay);
  };

  const handleTimeFrameChange = event => {
    setTimeFrame(event.target.value);
  };

  if (isFetchingEngagementReport) {
    return <ProgressFullscreen open />;
  }

  return (
    <>
      {marketCode && (
        <AppBar
          position="static"
          classes={{ root: classes.appBar }}
          elevation={1}
        >
          <MUIToolbar>
            <GoBackButton />
          </MUIToolbar>
        </AppBar>
      )}
      <div className={classes.container}>
        <div>
          <div className={classes.header}>
            <span className={classes.headerText}>
              Co-Workers Engagement Report {marketCode && `- (${marketCode})`}{' '}
              (Total Interactions: {totalCount})
            </span>
          </div>
          <div className={classes.paper}>
            <Grid container spacing={1}>
              <Grid item sm={3} md={3}>
                <MUIFormControl variant="filled" margin="normal" fullWidth>
                  <InputLabel>Select Year</InputLabel>
                  <MUISelect
                    value={year}
                    onChange={e => setYear(e.target.value)}
                  >
                    {years.map(year => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </MUISelect>
                </MUIFormControl>
              </Grid>
              <Grid item sm={3} md={3}>
                <MUIFormControl variant="filled" margin="normal" fullWidth>
                  <InputLabel>Select Period</InputLabel>
                  <MUISelect value={timeFrame} onChange={handleTimeFrameChange}>
                    <MenuItem value="monthly">Monthly</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="daily">Daily</MenuItem>
                  </MUISelect>
                </MUIFormControl>
              </Grid>
            </Grid>
            <div className={classes.chartWrapper}>
              <div
                className={`${classes.chartContainer} ${
                  timeFrame === 'daily' ? classes.extendedChart : ''
                }`}
              >
                {hasData ? (
                  <Bar
                    data={chartData}
                    options={{
                      maintainAspectRatio: false,
                      scales: {
                        x: {
                          title: {
                            display: true,
                            text:
                              timeFrame.charAt(0).toUpperCase() +
                              timeFrame.slice(1),
                            font: {
                              weight: 'bold' // Make X axis title bold
                            }
                          },
                          grid: {
                            display: true
                          }
                        },
                        y: {
                          title: {
                            display: true,
                            text: 'Interactions Count',
                            font: {
                              weight: 'bold' // Make X axis title bold
                            }
                          },
                          grid: {
                            display: true
                          }
                        }
                      },
                      plugins: {
                        tooltip: {
                          callbacks: {
                            title(tooltipItems) {
                              const index = tooltipItems[0].dataIndex;
                              const { label } = tooltipItems[0];
                              if (timeFrame === 'weekly') {
                                return weekRanges[index];
                              }
                              if (timeFrame === 'daily') {
                                return label;
                              }
                              return label;
                            },
                            label(tooltipItem) {
                              return `Interactions: ${tooltipItem.raw}`;
                            }
                          }
                        }
                      }
                    }}
                  />
                ) : (
                  <Typography className={classes.noDataText}>
                    No interaction data available for the selected year.
                  </Typography>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CoWorkerEngagementReportView;
